import { createContext, useContext, useState, useMemo, useRef, useEffect } from 'react';
import * as React from 'react';
import { OrgSettings, UpdateOrgSettings } from '@sdk/authentication/types/OrganisationType';

type UserOrgSettingsContextType = {
  userOrgSettings: OrgSettings;
  initialUserOrgSettings: OrgSettings;
  onUserOrgSettingsChange: UpdateOrgSettings;
  isUserOrgSettingsChanged: boolean;
  setIsUserOrgSettingsChanged: (value: boolean) => void;
  setInitialUserOrgSettings: (settings: OrgSettings) => void;
  revertOrgSettingsChanges: () => void;
};
const UserOrgSettingsContext = createContext<UserOrgSettingsContextType>({
  userOrgSettings: null,
  onUserOrgSettingsChange: null,
  isUserOrgSettingsChanged: null,
  setIsUserOrgSettingsChanged: null,
  revertOrgSettingsChanges: null,
  setInitialUserOrgSettings: null,
  initialUserOrgSettings: null,
});

type Props = {
  settings: OrgSettings;
};
const UserOrgSettingsProvider: React.FC<React.PropsWithChildren<Props>> = ({
  children,
  settings,
}) => {
  const initialUserOrgSettings = useRef(settings);
  const [userOrgSettings, setUserOrgSettings] = useState(settings);
  const [isUserOrgSettingsChanged, setIsUserOrgSettingsChanged] = useState(false);

  useEffect(() => {
    setUserOrgSettings(settings);
    initialUserOrgSettings.current = settings;
  }, [settings]);

  useEffect(() => {
    setIsUserOrgSettingsChanged(
      JSON.stringify(initialUserOrgSettings.current) !== JSON.stringify(userOrgSettings)
    );
  }, [userOrgSettings]);

  const value = useMemo(() => {
    const onUserOrgSettingsChange: UpdateOrgSettings = (updatedSettingsProperties): void => {
      const updatedUserOrgSettings = {
        ...userOrgSettings,
        ...updatedSettingsProperties,
      };

      setUserOrgSettings(updatedUserOrgSettings);
    };

    const revertOrgSettingsChanges = (): void => setUserOrgSettings(initialUserOrgSettings.current);

    const setInitialUserOrgSettings = (value: OrgSettings): OrgSettings =>
      (initialUserOrgSettings.current = value);

    return {
      userOrgSettings,
      onUserOrgSettingsChange,
      isUserOrgSettingsChanged,
      setIsUserOrgSettingsChanged,
      revertOrgSettingsChanges,
      setInitialUserOrgSettings,
      initialUserOrgSettings: initialUserOrgSettings.current,
    };
  }, [isUserOrgSettingsChanged, userOrgSettings]);

  return (
    <UserOrgSettingsContext.Provider value={value}>{children}</UserOrgSettingsContext.Provider>
  );
};

function useUserOrgSettings(): UserOrgSettingsContextType {
  const context = useContext(UserOrgSettingsContext);
  if (!context) {
    throw new Error('UserOrgSettingsContext must be used within a UserOrgSettingsProvider');
  }

  return context;
}

export { UserOrgSettingsProvider, useUserOrgSettings };
